import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';

// Register fonts for better typography - with error handling
try {
  Font.register({
    family: 'Roboto',
    fonts: [
      { src: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2', fontWeight: 'normal' },
      { src: 'https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlfBBc4.woff2', fontWeight: 'bold' },
    ]
  });
} catch (error) {
  console.warn('Font registration failed, using default fonts:', error);
}

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 20,
    fontFamily: 'Helvetica', // Use built-in font instead of external Roboto
    fontSize: 10,
  },
  
  // Header Section
  header: {
    flexDirection: 'row',
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 2,
    borderBottomColor: '#2563eb',
    borderBottomStyle: 'solid',
  },
  
  companySection: {
    flex: 2,
  },
  
  companyName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: 5,
  },
  
  companyDetails: {
    fontSize: 9,
    color: '#6b7280',
    lineHeight: 1.4,
  },
  
  invoiceSection: {
    flex: 1,
    alignItems: 'flex-end',
  },
  
  invoiceTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#dc2626',
    marginBottom: 10,
  },
  
  invoiceNumber: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 3,
  },
  
  invoiceDate: {
    fontSize: 10,
    color: '#6b7280',
  },
  
  // Customer Section
  customerSection: {
    flexDirection: 'row',
    marginBottom: 20,
    backgroundColor: '#f8fafc',
    padding: 15,
    borderRadius: 8,
  },
  
  billToSection: {
    flex: 1,
  },
  
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  
  customerName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 5,
  },
  
  customerDetails: {
    fontSize: 9,
    color: '#4b5563',
    lineHeight: 1.4,
  },
  
  // Items Table
  table: {
    marginBottom: 20,
  },
  
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#1e40af',
    paddingVertical: 8,
    paddingHorizontal: 5,
  },
  
  tableHeaderText: {
    color: '#ffffff',
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    borderBottomStyle: 'solid',
    paddingVertical: 8,
    paddingHorizontal: 5,
    minHeight: 30,
  },
  
  tableRowAlt: {
    backgroundColor: '#f9fafb',
  },
  
  tableCell: {
    fontSize: 9,
    color: '#374151',
    textAlign: 'center',
    paddingHorizontal: 2,
  },
  
  tableCellLeft: {
    textAlign: 'left',
  },
  
  tableCellRight: {
    textAlign: 'right',
  },
  
  // Column widths
  col1: { width: '5%' },   // S.No
  col2: { width: '35%' },  // Item Name
  col3: { width: '8%' },   // HSN
  col4: { width: '8%' },   // Qty
  col5: { width: '10%' },  // Rate
  col6: { width: '8%' },   // Disc%
  col7: { width: '8%' },   // GST%
  col8: { width: '9%' },   // Taxable
  col9: { width: '9%' },   // Total
  
  // Summary Section
  summarySection: {
    flexDirection: 'row',
    marginTop: 10,
  },
  
  summaryLeft: {
    flex: 1,
  },
  
  summaryRight: {
    width: 200,
    backgroundColor: '#f8fafc',
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderStyle: 'solid',
  },
  
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  
  summaryLabel: {
    fontSize: 9,
    color: '#6b7280',
  },
  
  summaryValue: {
    fontSize: 9,
    color: '#374151',
    fontWeight: 'bold',
  },
  
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#d1d5db',
    borderTopStyle: 'solid',
  },
  
  totalLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1e40af',
  },
  
  totalValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#dc2626',
  },
  
  // Payment Info
  paymentSection: {
    marginTop: 15,
    backgroundColor: '#fef3c7',
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#f59e0b',
    borderLeftStyle: 'solid',
  },
  
  paymentTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#92400e',
    marginBottom: 5,
  },
  
  paymentDetails: {
    fontSize: 9,
    color: '#78350f',
    lineHeight: 1.4,
  },
  
  // Footer
  footer: {
    marginTop: 30,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    borderTopStyle: 'solid',
  },
  
  footerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  
  footerColumn: {
    flex: 1,
    paddingHorizontal: 10,
  },
  
  footerTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 5,
  },
  
  footerText: {
    fontSize: 8,
    color: '#6b7280',
    lineHeight: 1.3,
  },
  
  signature: {
    marginTop: 20,
    alignItems: 'flex-end',
  },
  
  signatureText: {
    fontSize: 9,
    color: '#6b7280',
    marginBottom: 30,
  },
  
  signatureLine: {
    width: 150,
    borderBottomWidth: 1,
    borderBottomColor: '#9ca3af',
    borderBottomStyle: 'solid',
    marginBottom: 5,
  },
  
  signatureLabel: {
    fontSize: 8,
    color: '#6b7280',
    textAlign: 'center',
  },
  
  // Watermark
  watermark: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-45deg)',
    fontSize: 60,
    color: '#f3f4f6',
    opacity: 0.1,
    zIndex: -1,
  },
  
  // Status Badge
  statusBadge: {
    position: 'absolute',
    top: 100,
    right: 30,
    backgroundColor: '#10b981',
    color: '#ffffff',
    padding: 8,
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'center',
    transform: 'rotate(15deg)',
  },
  
  statusBadgePending: {
    backgroundColor: '#f59e0b',
  },
  
  statusBadgeOverdue: {
    backgroundColor: '#ef4444',
  },
});

const InvoicePDF = ({ saleData, saleDetails, customerData, companyData }) => {
  // Data validation and sanitization
  const safeSaleData = {
    BillNumber: saleData?.BillNumber || '0001',
    BillDate: saleData?.BillDate || new Date().toISOString(),
    CustomerName: saleData?.CustomerName || 'Customer Name',
    AddressLine1: saleData?.AddressLine1 || '',
    AddressLine2: saleData?.AddressLine2 || '',
    Location: saleData?.Location || '',
    PinCode: saleData?.PinCode || '',
    ContactNumber1: saleData?.ContactNumber1 || '',
    ContactNumber2: saleData?.ContactNumber2 || '',
    Amount: parseFloat(saleData?.Amount) || 0,
    DiscountAmount: parseFloat(saleData?.DiscountAmount) || 0,
    NetAmount: parseFloat(saleData?.NetAmount) || 0,
    CGSTAmount: parseFloat(saleData?.CGSTAmount) || 0,
    SGSTAmount: parseFloat(saleData?.SGSTAmount) || 0,
    IGSTAmount: parseFloat(saleData?.IGSTAmount) || 0,
    RoundOff: parseFloat(saleData?.RoundOff) || 0,
    TransportingCharge: parseFloat(saleData?.TransportingCharge) || 0,
    TotalAmount: parseFloat(saleData?.TotalAmount) || 0,
    PaidAmount: parseFloat(saleData?.PaidAmount) || 0,
    BalanceAmount: parseFloat(saleData?.BalanceAmount) || 0,
    ModeOfPayment: parseInt(saleData?.ModeOfPayment) || 0,
    Remarks: saleData?.Remarks || '',
    IsGST: saleData?.IsGST ?? true,
    FinYear: saleData?.FinYear || ''
  };

  const safeSaleDetails = Array.isArray(saleDetails) ? saleDetails.map(item => ({
    ItemName: item?.ItemName || `Item ${item?.ItemId || 'Unknown'}`,
    HSNCode: item?.HSNCode || '',
    Quantity: parseFloat(item?.Quantity) || 0,
    Rate: parseFloat(item?.Rate) || 0,
    Amount: parseFloat(item?.Amount) || 0,
    DiscountPercentage: parseFloat(item?.DiscountPercentage) || 0,
    DiscountAmount: parseFloat(item?.DiscountAmount) || 0,
    TaxableAmount: parseFloat(item?.TaxableAmount) || 0,
    CGSTAmount: parseFloat(item?.CGSTAmount) || 0,
    SGSTAmount: parseFloat(item?.SGSTAmount) || 0,
    IGSTAmount: parseFloat(item?.IGSTAmount) || 0,
    TotalAmount: parseFloat(item?.TotalAmount) || 0,
    GSTRate: parseFloat(item?.GSTRate) || 0
  })) : [];

  // Helper functions
  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return `₹${numAmount.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-IN', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch (error) {
      console.warn('Date formatting error:', error);
      return '-';
    }
  };

  const getPaymentStatus = () => {
    const balance = safeSaleData.BalanceAmount;
    const total = safeSaleData.TotalAmount;
    
    if (balance <= 0) return { status: 'PAID', color: '#10b981' };
    if (balance === total) return { status: 'UNPAID', color: '#ef4444' };
    return { status: 'PARTIAL', color: '#f59e0b' };
  };

  const paymentStatus = getPaymentStatus();

  const getModeOfPaymentText = (mode) => {
    const modes = {
      0: 'Cash',
      1: 'Card',
      2: 'UPI',
      3: 'Bank Transfer',
      4: 'Cheque'
    };
    return modes[mode] || 'Cash';
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Watermark */}
        <Text style={styles.watermark}>INVOICE</Text>
        
        {/* Status Badge */}
        <View style={[
          styles.statusBadge,
          paymentStatus.status === 'PARTIAL' && styles.statusBadgePending,
          paymentStatus.status === 'UNPAID' && styles.statusBadgeOverdue
        ]}>
          <Text>{paymentStatus.status}</Text>
        </View>

        {/* Header */}
        <View style={styles.header}>
          <View style={styles.companySection}>
            <Text style={styles.companyName}>
              {companyData?.name || 'Your Company Name'}
            </Text>
            <Text style={styles.companyDetails}>
              {companyData?.address || 'Company Address Line 1'}{'\n'}
              {companyData?.city || 'City'}, {companyData?.state || 'State'} - {companyData?.pincode || '000000'}{'\n'}
              Phone: {companyData?.phone || '+91 00000 00000'} | Email: {companyData?.email || 'info@company.com'}{'\n'}
              {companyData?.gst && `GST: ${companyData.gst}`} | {companyData?.pan && `PAN: ${companyData.pan}`}
            </Text>
          </View>
          
          <View style={styles.invoiceSection}>
            <Text style={styles.invoiceTitle}>INVOICE</Text>
            <Text style={styles.invoiceNumber}>
              #{safeSaleData.BillNumber}
            </Text>
            <Text style={styles.invoiceDate}>
              Date: {formatDate(safeSaleData.BillDate)}
            </Text>
            {safeSaleData.FinYear && (
              <Text style={styles.invoiceDate}>
                FY: {safeSaleData.FinYear}
              </Text>
            )}
          </View>
        </View>

        {/* Customer Section */}
        <View style={styles.customerSection}>
          <View style={styles.billToSection}>
            <Text style={styles.sectionTitle}>Bill To</Text>
            <Text style={styles.customerName}>
              {safeSaleData.CustomerName}
            </Text>
            <Text style={styles.customerDetails}>
              {safeSaleData.AddressLine1 && `${safeSaleData.AddressLine1}\n`}
              {safeSaleData.AddressLine2 && `${safeSaleData.AddressLine2}\n`}
              {safeSaleData.Location && `${safeSaleData.Location}, `}
              {safeSaleData.PinCode && `${safeSaleData.PinCode}\n`}
              {safeSaleData.ContactNumber1 && `Phone: ${safeSaleData.ContactNumber1}`}
              {safeSaleData.ContactNumber2 && ` | ${safeSaleData.ContactNumber2}`}
            </Text>
          </View>
          
          <View style={styles.billToSection}>
            <Text style={styles.sectionTitle}>Invoice Details</Text>
            <Text style={styles.customerDetails}>
              Invoice Date: {formatDate(safeSaleData.BillDate)}{'\n'}
              Payment Mode: {getModeOfPaymentText(safeSaleData.ModeOfPayment)}{'\n'}              
              {safeSaleData.Remarks && `Remarks: ${safeSaleData.Remarks}`}
            </Text>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.col1]}>S.No</Text>
            <Text style={[styles.tableHeaderText, styles.col2, styles.tableCellLeft]}>Item Description</Text>
            <Text style={[styles.tableHeaderText, styles.col3]}>HSN</Text>
            <Text style={[styles.tableHeaderText, styles.col4]}>Qty</Text>
            <Text style={[styles.tableHeaderText, styles.col5]}>Rate</Text>
            <Text style={[styles.tableHeaderText, styles.col6]}>Disc%</Text>
            <Text style={[styles.tableHeaderText, styles.col7]}>GST%</Text>
            <Text style={[styles.tableHeaderText, styles.col8]}>Taxable</Text>
            <Text style={[styles.tableHeaderText, styles.col9]}>Total</Text>
          </View>

          {/* Table Rows */}
          {safeSaleDetails.map((item, index) => (
            <View key={index} style={[
              styles.tableRow,
              index % 2 === 1 && styles.tableRowAlt
            ]}>
              <Text style={[styles.tableCell, styles.col1]}>{index + 1}</Text>
              <Text style={[styles.tableCell, styles.col2, styles.tableCellLeft]}>
                {item.ItemName}
              </Text>
              <Text style={[styles.tableCell, styles.col3]}>{item.HSNCode || '-'}</Text>
              <Text style={[styles.tableCell, styles.col4]}>{item.Quantity}</Text>
              <Text style={[styles.tableCell, styles.col5, styles.tableCellRight]}>
                {(item.Rate)}
              </Text>
              <Text style={[styles.tableCell, styles.col6]}>{item.DiscountPercentage}%</Text>
              <Text style={[styles.tableCell, styles.col7]}>{item.GSTRate}%</Text>
              <Text style={[styles.tableCell, styles.col8, styles.tableCellRight]}>
                {(item.TaxableAmount)}
              </Text>
              <Text style={[styles.tableCell, styles.col9, styles.tableCellRight]}>
                {(item.TotalAmount)}
              </Text>
            </View>
          ))}
        </View>

        {/* Summary Section */}
        <View style={styles.summarySection}>
          <View style={styles.summaryLeft}>
            {/* Payment Information */}
            <View style={styles.paymentSection}>
              <Text style={styles.paymentTitle}>Payment Information</Text>
              <Text style={styles.paymentDetails}>
                Payment Mode: {getModeOfPaymentText(safeSaleData.ModeOfPayment)}{'\n'}
                Total Amount: {(safeSaleData.TotalAmount)}{'\n'}
                Paid Amount: {(safeSaleData.PaidAmount)}{'\n'}
                Balance Amount: {(safeSaleData.BalanceAmount)}{'\n'}
                Status: {paymentStatus.status}
              </Text>
            </View>
          </View>

          <View style={styles.summaryRight}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Subtotal:</Text>
              <Text style={styles.summaryValue}>{(safeSaleData.Amount)}</Text>
            </View>
            
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Discount:</Text>
              <Text style={styles.summaryValue}>-{(safeSaleData.DiscountAmount)}</Text>
            </View>
            
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Net Amount:</Text>
              <Text style={styles.summaryValue}>{(safeSaleData.NetAmount)}</Text>
            </View>
            
            {safeSaleData.IsGST && (
              <>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>CGST:</Text>
                  <Text style={styles.summaryValue}>{(safeSaleData.CGSTAmount)}</Text>
                </View>
                
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>SGST:</Text>
                  <Text style={styles.summaryValue}>{(safeSaleData.SGSTAmount)}</Text>
                </View>
                
                {safeSaleData.IGSTAmount > 0 && (
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>IGST:</Text>
                    <Text style={styles.summaryValue}>{(safeSaleData.IGSTAmount)}</Text>
                  </View>
                )}
              </>
            )}
            
            {safeSaleData.TransportingCharge > 0 && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Transport:</Text>
                <Text style={styles.summaryValue}>{(safeSaleData.TransportingCharge)}</Text>
              </View>
            )}
            
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Round Off:</Text>
              <Text style={styles.summaryValue}>
                {safeSaleData.RoundOff >= 0 ? '+' : ''}{safeSaleData.RoundOff.toFixed(2)}
              </Text>
            </View>
            
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Total Amount:</Text>
              <Text style={styles.totalValue}>{(safeSaleData.TotalAmount)}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <View style={styles.footerSection}>
            <View style={styles.footerColumn}>
              <Text style={styles.footerTitle}>Terms & Conditions</Text>
              <Text style={styles.footerText}>
                1. Payment is due within 30 days of invoice date.{'\n'}
                2. Late payments may incur additional charges.{'\n'}
                3. Goods once sold will not be taken back.{'\n'}
                4. Subject to local jurisdiction only.
              </Text>
            </View>
            
            <View style={styles.footerColumn}>
              <Text style={styles.footerTitle}>Bank Details</Text>
              <Text style={styles.footerText}>
                Bank Name: {companyData?.bankName || 'Your Bank Name'}{'\n'}
                Account No: {companyData?.accountNo || 'XXXX XXXX XXXX'}{'\n'}
                IFSC Code: {companyData?.ifscCode || 'XXXX0000XXX'}{'\n'}
                Branch: {companyData?.branch || 'Branch Name'}
              </Text>
            </View>
            
            <View style={styles.footerColumn}>
              <Text style={styles.footerTitle}>Contact Information</Text>
              <Text style={styles.footerText}>
                Email: {companyData?.email || 'info@company.com'}{'\n'}
                Phone: {companyData?.phone || '+91 00000 00000'}{'\n'}                
              </Text>
            </View>
          </View>
          
          {/* Signature */}
          <View style={styles.signature}>
            <Text style={styles.signatureText}>For {companyData?.name || 'Your Company Name'}</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Authorized Signatory</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;